import type { IdGen } from '../model/ids';
import type { KinklistDoc } from '../model/types';
import type { History } from './history';
import { amend, createHistory, HISTORY_LIMIT, push, redo, undo } from './history';

export type Op = (doc: KinklistDoc) => KinklistDoc;

export interface DocStoreOptions {
  initial: KinklistDoc;
  newId: IdGen;
  now: () => Date;
  /** Called after every change, including undo/redo (autosave hooks in here). */
  onChange?: (doc: KinklistDoc) => void;
  historyLimit?: number;
  /** Consecutive ops with the same `coalesce` key within this window share one undo step. */
  coalesceMs?: number;
}

/**
 * The single source of truth for the current list. Every change goes through
 * a pure op from `model/ops.ts`; snapshots are immutable, so undo/redo is a
 * matter of keeping references.
 */
export class DocStore {
  #history: History<KinklistDoc>;
  #last: { key: string; at: number } | null = null;
  /** The document before the current gesture (drag), if one is in progress. */
  #gestureBase: KinklistDoc | null = null;
  readonly #options: DocStoreOptions;
  readonly newId: IdGen;

  constructor(options: DocStoreOptions) {
    this.#options = options;
    this.newId = options.newId;
    this.#history = $state.raw(createHistory(options.initial));
  }

  get doc(): KinklistDoc {
    return this.#history.present;
  }

  get canUndo(): boolean {
    return this.#history.past.length > 0;
  }

  get canRedo(): boolean {
    return this.#history.future.length > 0;
  }

  /**
   * Applies `op`; returns false (and records nothing) when it was a no-op.
   * `coalesce` merges rapid same-key changes into one undo step; `record: false`
   * keeps view-only changes (collapsing a card) out of the undo history.
   */
  apply(op: Op, options: { coalesce?: string; record?: boolean } = {}): boolean {
    const current = this.#history.present;
    const next = op(current);
    if (next === current) return false;
    const now = this.#options.now();
    const stamped = { ...next, updatedAt: now.toISOString() };
    const key = options.coalesce;
    const window = this.#options.coalesceMs ?? 1000;
    const coalesced =
      key !== undefined &&
      this.#last?.key === key &&
      now.getTime() - this.#last.at <= window &&
      this.canUndo;
    this.#history =
      coalesced || options.record === false || this.#gestureBase !== null
        ? amend(this.#history, stamped)
        : push(this.#history, stamped, this.#options.historyLimit ?? HISTORY_LIMIT);
    this.#last = key === undefined ? null : { key, at: now.getTime() };
    this.#options.onChange?.(stamped);
    return true;
  }

  /** Replaces the whole list (import, reset). Undoable within the session. */
  replace(doc: KinklistDoc): void {
    this.apply(() => doc);
  }

  /**
   * Starts a gesture (e.g. dragging a card): changes until `endGesture` are
   * previews that become a single undo step on commit, or are reverted.
   */
  beginGesture(): void {
    this.#gestureBase ??= this.#history.present;
    this.#last = null;
  }

  endGesture(commit: boolean): void {
    const base = this.#gestureBase;
    this.#gestureBase = null;
    if (base === null || base === this.#history.present) return;
    if (commit) {
      const { past, present } = this.#history;
      this.#history = push({ past, present: base, future: [] }, present, this.#limit);
    } else {
      this.#history = amend(this.#history, base);
      this.#options.onChange?.(base);
    }
  }

  get #limit(): number {
    return this.#options.historyLimit ?? HISTORY_LIMIT;
  }

  /**
   * Takes over a list changed elsewhere (another tab) as an undoable step,
   * without notifying `onChange` — it is already saved.
   */
  adopt(doc: KinklistDoc): void {
    if (doc === this.#history.present) return;
    this.#history = push(this.#history, doc, this.#options.historyLimit ?? HISTORY_LIMIT);
    this.#last = null;
  }

  undo(): boolean {
    return this.#travel(undo);
  }

  redo(): boolean {
    return this.#travel(redo);
  }

  #travel(step: (h: History<KinklistDoc>) => History<KinklistDoc>): boolean {
    const next = step(this.#history);
    if (next === this.#history) return false;
    this.#history = next;
    this.#last = null;
    this.#options.onChange?.(next.present);
    return true;
  }
}
